import {
    INVESTIGATION_ENTITY_TYPES,
    INVESTIGATION_USER_TYPES,
    RISK_LEVELS
} from '@silverfort-nodelibs/common/utils/consts';
import { uppercaseFirstLetter } from '../utils';
import { ReactComponent as UserIcon } from '../common/Images/user_blue_back.svg';
import { ReactComponent as UserDeletedIcon } from '../common/Images/user_blue_back_deleted.svg';
import { ReactComponent as UserWarningIcon } from '../common/Images/user_blue_back_warning.svg';
import { ReactComponent as ServerIcon } from '../common/Images/server_blue_back.svg';
import { ReactComponent as ServerDeletedIcon } from '../common/Images/server_blue_back_deleted.svg';
import { ReactComponent as ServerWarningIcon } from '../common/Images/server_blue_back_warning.svg';
import { ReactComponent as DesktopIcon } from '../common/Images/desktop_blue.svg';
import { ReactComponent as DesktopDeletedIcon } from '../common/Images/desktop_blue_deleted.svg';
import { ReactComponent as DesktopWarningIcon } from '../common/Images/desktop_blue_warning.svg';
import { ReactComponent as AzureIcon } from '../common/Images/azure_blue_back.svg';
import { ReactComponent as AzureDeletedIcon } from '../common/Images/azure_blue_back_deleted.svg';
import { ReactComponent as AzureWarningIcon } from '../common/Images/azure_blue_back_warning.svg';
import { ReactComponent as OktaIcon } from '../common/Images/okta_blue_back.svg';
import { ReactComponent as OktaDeletedIcon } from '../common/Images/okta_blue_back_deleted.svg';
import { ReactComponent as OktaWarningIcon } from '../common/Images/okta_blue_back_warning.svg';
import InvestigateIcon from '../common/Images/investigation_white.png';

import style from './EntityId.module.css';
import { useAppDrawer } from '../AppDrawer/AppDrawerProvider';
import InvestigationDrawer from '../InvestigationDrawer/InvestigationDrawer';
import React from 'react';

const USER_ACCOUNT_CONTROL_FLAGS = {
    ACCOUNTDISABLE: {
        VALUE: 2,
        NAME: 'Disabled account'
    },
    LOCKOUT: {
        VALUE: 16,
        NAME: 'Locked'
    },
    PASSWD_NOTREQD: {
        VALUE: 32,
        NAME: 'Password not required'
    },
    PASSWD_CANT_CHANGE: {
        VALUE: 64,
        NAME: 'Password can not changed'
    },
    TRUSTED_FOR_DELEGATION: {
        VALUE: 524288,
        NAME: 'Trusted for delegation'
    },
    PASSWORD_EXPIRED: {
        VALUE: 8388608,
        NAME: 'Password expired'
    }
};

const ICON_TYPES = {
    ICON: 'icon',
    DELETED_ICON: 'deletedIcon',
    WARNING_ICON: 'warningIcon'
};

const ENTITY_TO_ICON_SETTINGS = {
    [INVESTIGATION_ENTITY_TYPES.USER.id]: {
        [ICON_TYPES.ICON]: {
            icon: <UserIcon className="h-14 w-14 rounded-[50%] mb-1 text-sf-primary-500 fill-sf-dark-500" />
        },
        [ICON_TYPES.DELETED_ICON]: {
            icon: <UserDeletedIcon className="h-16 w-16 mb-1 ml-2 text-sf-primary-500 fill-sf-dark-500" />
        },
        [ICON_TYPES.WARNING_ICON]: {
            icon: <UserWarningIcon className="h-16 w-16 mb-1 ml-2 text-sf-primary-500 fill-sf-dark-500" />
        }
    },
    [INVESTIGATION_ENTITY_TYPES.DEVICE.id]: {
        [ICON_TYPES.ICON]: {
            icon: <DesktopIcon className="h-14 w-14 rounded-[50%] mb-1 text-sf-primary-500 fill-sf-dark-500" />
        },
        [ICON_TYPES.DELETED_ICON]: {
            icon: <DesktopDeletedIcon className="h-16 w-16 mb-1 ml-2 text-sf-primary-500 fill-sf-dark-500" />
        },
        [ICON_TYPES.WARNING_ICON]: {
            icon: <DesktopWarningIcon className="h-16 w-16 mb-1 ml-2 text-sf-primary-500 fill-sf-dark-500" />
        }
    },
    [INVESTIGATION_ENTITY_TYPES.RESOURCE.id]: {
        [ICON_TYPES.ICON]: {
            icon: <ServerIcon className="h-14 w-14 rounded-[50%] mb-1 text-sf-primary-500 fill-sf-dark-500" />
        },
        [ICON_TYPES.DELETED_ICON]: {
            icon: <ServerDeletedIcon className="h-16 w-16 mb-1 ml-2 text-sf-primary-500 fill-sf-dark-500" />
        },
        [ICON_TYPES.WARNING_ICON]: {
            icon: <ServerWarningIcon className="h-16 w-16 mb-1 ml-2 text-sf-primary-500 fill-sf-dark-500" />
        }
    },
    [INVESTIGATION_ENTITY_TYPES.APPLICATION.id]: {
        [ICON_TYPES.ICON]: {
            icon: <AzureIcon className="h-14 w-14 rounded-[50%] mb-1 text-sf-primary-500 fill-sf-dark-500" />
        },
        [ICON_TYPES.DELETED_ICON]: {
            icon: <AzureDeletedIcon className="h-16 w-16 mb-1 ml-2 text-sf-primary-500 fill-sf-dark-500" />
        },
        [ICON_TYPES.WARNING_ICON]: {
            icon: <AzureWarningIcon className="h-16 w-16 mb-1 ml-2 text-sf-primary-500 fill-sf-dark-500" />
        }
    },
    [INVESTIGATION_ENTITY_TYPES.OKTA.id]: {
        [ICON_TYPES.ICON]: {
            icon: <OktaIcon className="h-14 w-14 rounded-[50%] mb-1 text-sf-primary-500 fill-sf-dark-500" />
        },
        [ICON_TYPES.DELETED_ICON]: {
            icon: <OktaDeletedIcon className="h-16 w-16 mb-1 ml-2 text-sf-primary-500 fill-sf-dark-500" />
        },
        [ICON_TYPES.WARNING_ICON]: {
            icon: <OktaWarningIcon className="h-16 w-16 mb-1 ml-2 text-sf-primary-500 fill-sf-dark-500" />
        }
    }
};

const EntityId = ({ entityDetails, entityType, showGoToInvestigation = false }) => {
    const { displayContent } = useAppDrawer();
    const risk = Object.values(RISK_LEVELS).find((level) => level.value === entityDetails.riskLevel);
    const entityRisk = risk ? risk.label : RISK_LEVELS.LOW.label;
    const isAdEntity =
        entityType.id !== INVESTIGATION_ENTITY_TYPES.USER.id ||
        entityDetails.type === INVESTIGATION_USER_TYPES.HYBRID ||
        entityDetails.type === INVESTIGATION_USER_TYPES.AD;
    const flags = [];
    for (const flagKey of Object.keys(USER_ACCOUNT_CONTROL_FLAGS)) {
        if (
            isAdEntity &&
            (entityDetails.user_account_control & USER_ACCOUNT_CONTROL_FLAGS[flagKey].VALUE ||
                (USER_ACCOUNT_CONTROL_FLAGS.ACCOUNTDISABLE.VALUE === USER_ACCOUNT_CONTROL_FLAGS[flagKey].VALUE &&
                    !entityDetails.user_account_control))
        ) {
            flags.push(USER_ACCOUNT_CONTROL_FLAGS[flagKey].NAME);
        }
    }

    let iconType = ICON_TYPES.ICON;
    if (entityDetails.is_deleted) {
        iconType = ICON_TYPES.DELETED_ICON;
    } else if (flags.length) {
        iconType = ICON_TYPES.WARNING_ICON;
    }
    const iconSettings = ENTITY_TO_ICON_SETTINGS[entityType.id][iconType];
    if (entityType.id === INVESTIGATION_ENTITY_TYPES.APPLICATION.id && entityDetails.logo) {
        iconSettings.icon = entityDetails.logo;
    }

    let ouName = '';
    try {
        ouName = entityDetails.path.split('\\')[0];
    } catch (ex) {}

    return (
        <div className={style.warper}>
            {iconSettings.icon}
            <div className={style.fieldWraper} title={entityDetails.display_name}>
                <div className={style.displayName}>
                    <span>{entityDetails.display_name}</span>
                    {entityType.id === INVESTIGATION_ENTITY_TYPES.USER.id && entityDetails.type && (
                        <span className="bg-white text-sf-dark-500 text-xs font-semibold rounded-lg p-1 ml-2 ">
                            {entityDetails.type}
                        </span>
                    )}
                </div>
                {entityDetails.is_deleted && <div className={style.deleted}>(deleted)</div>}
            </div>
            {!!flags.length && (
                <div className={style.fieldWraper}>
                    <div className={style.flags} title={flags.join(', ')}>{`(${flags.join(', ')})`}</div>
                </div>
            )}
            {ouName && (
                <div className={style.fieldWraper} title={ouName}>
                    <div className={style.ouName}>{ouName}</div>
                </div>
            )}
            <div className="flex gap-1">
                <div className={`${style.risk} ${style[entityRisk]}`}>{uppercaseFirstLetter(entityRisk)} Risk</div>
                {showGoToInvestigation && (
                    <span
                        className="flex items-center justify-center w-8 h-8 mt-[3px] bg-sf-primary rounded"
                        title={'Investigate'}
                        rel="noopener noreferrer"
                        onClick={() =>
                            displayContent(
                                <InvestigationDrawer
                                    guid={entityDetails.guid}
                                    identifier={entityDetails.display_name}
                                    adType={entityType.adType}
                                />
                            )
                        }>
                        <img src={InvestigateIcon} className="w-5 h-5" alt="investigate icon" />
                    </span>
                )}
            </div>
        </div>
    );
};

export default EntityId;
